import type { Idp, IdpFinding } from '@/api.ts';
import type { Role } from '@/session.tsx';

/** The status row above the connection form: one chip, then the two facts that qualify it. */
export interface ConnectionLine {
  /** The chip's word. */
  label: string;
  tone: 'connected' | 'pending' | 'error' | 'absent';
  /** What the chip does not say on its own. Empty when there is nothing configured to describe. */
  detail: string;
}

/**
 * What the status row says about the stored connection.
 *
 * The design asks for a "last verified" timestamp beside the chip. **There is no such field.**
 * `Idp` carries `updatedAt`, which is when the configuration was last *written* — a connection
 * saved on Monday and broken on Tuesday still has Monday's timestamp. Labelling that "verified"
 * would be a claim the control plane never made, and the one page where an operator most needs
 * to trust what they are reading. So it is labelled "saved", and Test sign-in is what produces
 * a verification, in the panel below the form where the round-trip actually happened.
 */
export function connectionLine(idp: Idp | null): ConnectionLine {
  if (!idp) {
    return {
      label: 'not connected',
      tone: 'absent',
      detail: 'Shells render anonymously until a directory is federated.',
    };
  }

  const saved = `saved ${new Date(idp.updatedAt).toLocaleString()}`;
  if (idp.status === 'error') {
    return { label: 'error', tone: 'error', detail: idp.lastError ?? saved };
  }
  if (idp.status === 'pending') {
    return { label: 'pending', tone: 'pending', detail: `${saved} · pool ${idp.poolId}` };
  }
  return { label: 'connected', tone: 'connected', detail: `${saved} · pool ${idp.poolId}` };
}

/**
 * The worst thing in a set of findings, or `null` for a clean one.
 *
 * Drives one alert rather than one per finding, and it has to be the *worst* rather than the
 * first: an errors-and-warnings response sorted warning-first would otherwise render as a warning
 * and let a configuration that cannot work look merely untidy.
 */
export function worstSeverity(findings: readonly IdpFinding[]): 'error' | 'warning' | null {
  if (findings.some((finding) => finding.severity === 'error')) return 'error';
  if (findings.length > 0) return 'warning';
  return null;
}

/**
 * What a successful Test sign-in is allowed to claim.
 *
 * The design's success panel reports "the claims received". `POST /idp/test` returns
 * `{ ok, findings }` and no claims, so there are none to report and inventing a claims list would
 * be inventing evidence. The round-trip time is measured here, on the clock that actually timed
 * it, and is therefore the one number on the panel that is true.
 */
export function testSummary(ok: boolean, elapsedMs: number, findings: readonly IdpFinding[]): string {
  const took = `${Math.round(elapsedMs)}ms`;
  if (!ok) {
    const count = findings.length;
    return `The control plane refused this configuration in ${took} — ${count} ${count === 1 ? 'finding' : 'findings'} below.`;
  }
  return findings.length === 0
    ? `The control plane accepted this configuration in ${took}, with nothing to flag.`
    : `The control plane accepted this configuration in ${took}, with ${findings.length} ${findings.length === 1 ? 'warning' : 'warnings'} below.`;
}

/**
 * What each role reaches, as the reference beside the map.
 *
 * `anonymous` is on the list and is not a role anyone can be mapped to — it is what a visitor gets
 * when no listed group matches, which is the outcome operators misread most often. A group left
 * off the map does not fail closed with an error; it composes the public shell and shows the
 * request-access tile, which looks like the site working rather than like access being denied.
 */
export const ROLE_REACH: readonly { role: Role | 'anonymous'; reaches: string }[] = [
  { role: 'owner', reaches: 'Everything, including billing, federation, and deleting the organization.' },
  { role: 'admin', reaches: 'Everything except billing and deleting the organization.' },
  { role: 'editor', reaches: 'Publishes snapshots, edits shells and layouts. Cannot change who gets in.' },
  { role: 'viewer', reaches: 'Reads every page in this console and changes nothing.' },
  { role: 'anonymous', reaches: 'Not a mapping — what someone in no listed group gets. They compose the public shell and see the request-access tile.' },
];
